import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ROUTES } from '@constants'

function useInView(threshold = 0.12) {
  const ref = useRef(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); observer.disconnect() } },
      { threshold }
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [threshold])
  return [ref, inView]
}

const imagePacks = [
  {
    id: 1, name: 'Starter', label: '3 Images', price: 100, images: 3, duration: '2 jours',
    color: '#6B7280',
    features: ['3 images par annonce', 'Visibilité 2 jours', 'Accès tableau de bord', 'Support standard']
  },
  {
    id: 2, name: 'Pro', label: '6 Images', price: 200, images: 6, duration: '2 jours', popular: true,
    color: '#002A4E',
    features: ['6 images par annonce', 'Visibilité 2 jours', 'Mise en avant résultats', 'Support prioritaire', 'Statistiques annonce']
  },
  {
    id: 3, name: 'Elite', label: '9 Images', price: 300, images: 9, duration: '2 jours',
    color: '#FFD700',
    features: ['9 images par annonce', 'Visibilité 2 jours', 'Mise en avant premium', 'Support VIP 24/7', 'Statistiques avancées', 'Badge "Annonce Pro"']
  }
]

const videoPacks = [
  {
    id: 1, name: 'Standard', label: 'Pack Vidéo', price: 1500, duration: '2 semaines', color: '#6B7280',
    features: ['1 vidéo jusqu\'à 2 min', 'Visibilité 2 semaines', 'Miniature personnalisée', 'Support standard']
  },
  {
    id: 2, name: 'Pro', label: 'Pack Vidéo Pro', price: 2500, duration: '1 mois', popular: true, color: '#002A4E',
    features: ['1 vidéo jusqu\'à 5 min', 'Visibilité 1 mois', 'Miniature personnalisée', 'Mise en avant résultats', 'Support prioritaire']
  },
  {
    id: 3, name: 'Premium', label: 'Pack Vidéo Premium', price: 4000, duration: '2 mois', color: '#FFD700',
    features: ['1 vidéo jusqu\'à 10 min', 'Visibilité 2 mois', 'Miniature personnalisée', 'Support VIP', 'Statistiques détaillées', 'Badge "Vidéo Pro"']
  }
]

const bannerPacks = [
  {
    id: 1, name: 'Standard', label: 'Bannière', price: 5000, duration: '1 semaine', color: '#6B7280',
    features: ['Affichage 1 semaine', 'Position standard', 'Lien personnalisé', 'Stats de clics']
  },
  {
    id: 2, name: 'Premium', label: 'Bannière Premium', price: 12000, duration: '1 mois', popular: true, color: '#002A4E',
    features: ['Affichage 1 mois', 'Position premium (haut)', 'Lien personnalisé', 'Stats détaillées', 'Rotation prioritaire']
  },
  {
    id: 3, name: 'Elite', label: 'Bannière Elite', price: 30000, duration: '3 mois', color: '#FFD700',
    features: ['Affichage 3 mois', 'Position elite (1ère page)', 'Lien personnalisé', 'Stats temps réel', 'Rotation exclusive', 'Support dédié', 'Rapport mensuel']
  }
]

const TAB_ICONS = {
  images:  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>,
  videos:  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M4 6h8a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2z"/></svg>,
  banners: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>,
}

const TABS = [
  { key: 'images',  label: 'Images',    packs: imagePacks,  desc: 'Publiez vos produits avec des images attractives', route: ROUTES.CREATE_POST },
  { key: 'videos',  label: 'Vidéos',    packs: videoPacks,  desc: 'Créez des annonces vidéo percutantes', route: ROUTES.CREATE_VIDEO },
  { key: 'banners', label: 'Bannières', packs: bannerPacks, desc: 'Maximisez votre visibilité sur la plateforme', route: ROUTES.CREATE_BANNER }
]

const CheckIcon = ({ gold }) => (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke={gold ? '#FFD700' : '#002A4E'} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
  </svg>
)

const PricingPage = () => {
  const [activeTab, setActiveTab] = useState('images')
  const navigate = useNavigate()
  const current = TABS.find(t => t.key === activeTab)

  const [refHero,   inHero]   = useInView(0.01)
  const [refCards,  inCards]  = useInView(0.08)
  const [refBadges, inBadges] = useInView(0.1)
  const [refCta,    inCta]    = useInView(0.1)

  const handleSelectPack = (pack) => {
    localStorage.setItem('selectedPack', JSON.stringify({ type: activeTab, pack }))
    navigate(current.route)
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0F2F5' }}>

      {/* ── HERO ─────────────────────────────────────── */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #001a33 0%, #002A4E 50%, #003d6b 100%)' }}>
        {/* Décorations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,215,0,0.12) 0%, transparent 70%)' }}></div>
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,215,0,0.08) 0%, transparent 70%)' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-px opacity-10" style={{ background: 'linear-gradient(90deg, transparent, #FFD700, transparent)' }}></div>
        </div>

        <div ref={refHero} className="w-full md:w-[90%] lg:w-[80%] mx-auto px-4 py-20 relative z-10">
          <div className="text-center">
            <span className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold mb-6 border anim-hidden ${inHero ? 'animate-fade-in-up' : ''}`} style={{ backgroundColor: 'rgba(255,215,0,0.1)', color: '#FFD700', borderColor: 'rgba(255,215,0,0.3)' }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#FFD700' }}></span>
              Offres disponibles maintenant
            </span>
            <h1 className={`text-5xl lg:text-7xl font-black text-white mb-6 leading-none tracking-tight anim-hidden ${inHero ? 'animate-fade-in-up delay-200' : ''}`}>
              Tarifs &amp; 
              <span style={{ color: '#FFD700' }}> Forfaits</span>
            </h1>
            <p className={`text-gray-400 text-xl max-w-lg mx-auto mb-10 anim-hidden ${inHero ? 'animate-fade-in-up delay-300' : ''}`}>
              Des packs simples et transparents pour booster votre visibilité dès aujourd'hui.
            </p>
            {/* Stats */}
            <div className={`flex flex-wrap justify-center gap-8 anim-hidden ${inHero ? 'animate-fade-in-up delay-400' : ''}`}>
              {[['500+', 'Clients actifs'], ['1000+', 'Annonces publiées'], ['98%', 'Satisfaction'], ['3', 'Types de packs']].map(([val, lbl]) => (
                <div key={lbl} className="text-center">
                  <div className="text-3xl font-black" style={{ color: '#FFD700' }}>{val}</div>
                  <div className="text-gray-400 text-sm mt-1">{lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Vague de transition */}
        <div className="absolute bottom-0 left-0 right-0 h-8 overflow-hidden">
          <svg viewBox="0 0 1200 40" preserveAspectRatio="none" className="w-full h-full" fill="#F0F2F5">
            <path d="M0,40 C300,0 900,40 1200,10 L1200,40 Z"/>
          </svg>
        </div>
      </div>

      {/* ── TABS ─────────────────────────────────────── */}
      <div className="sticky top-0 z-30" style={{ backgroundColor: '#F0F2F5' }}>
        <div className="w-full md:w-[90%] lg:w-[80%] mx-auto px-4 py-5">
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-1 p-1.5 rounded-2xl shadow-lg" style={{ backgroundColor: 'white' }}>
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2.5 px-7 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                    activeTab === tab.key ? 'font-bold shadow-md scale-105' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                  style={activeTab === tab.key ? { backgroundColor: '#FFD700', color: '#002A4E' } : {}}
                >
                  <span className={activeTab === tab.key ? 'text-[#002A4E]' : 'text-gray-500'}>
                    {TAB_ICONS[tab.key]}
                  </span>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          {/* Description sous tabs */}
          <p className="text-center text-gray-500 text-sm mt-3">{current.desc}</p>
        </div>
      </div>

      {/* ── CARDS ────────────────────────────────────── */}
      <div className="w-full md:w-[90%] lg:w-[80%] mx-auto px-4 pb-16">

        <div ref={refCards} className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start mb-12">
          {current.packs.map((pack, i) => (
            <div
              key={pack.id}
              className={`relative rounded-3xl overflow-hidden transition-all duration-300 group anim-hidden ${inCards ? `animate-scale-in ${i===0?'delay-100':i===1?'delay-300':'delay-500'}` : ''} ${
                pack.popular
                  ? 'shadow-2xl ring-2 ring-offset-2 ring-offset-[#F0F2F5]'
                  : 'shadow-md hover:shadow-xl hover:-translate-y-1'
              }`}
              style={pack.popular ? { ringColor: '#FFD700' } : {}}
            >
              {/* Fond carte */}
              {pack.popular ? (
                <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, #001a33 0%, #002A4E 40%, #003d6b 100%)' }}></div>
              ) : (
                <div className="absolute inset-0 bg-white"></div>
              )}

              {/* Ruban populaire */}
              {pack.popular && (
                <div className="absolute top-5 -right-8 z-10 rotate-45 px-10 py-1 text-xs font-black tracking-wider" style={{ backgroundColor: '#FFD700', color: '#002A4E' }}>
                  POPULAIRE
                </div>
              )}

              <div className="relative z-10 p-8">
                {/* Header carte */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-widest mb-1 block" style={{ color: pack.popular ? 'rgba(255,215,0,0.7)' : '#9CA3AF' }}>
                      {pack.name}
                    </span>
                    <h3 className="text-2xl font-black" style={{ color: pack.popular ? '#FFD700' : '#002A4E' }}>
                      {pack.label}
                    </h3>
                  </div>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: pack.popular ? 'rgba(255,215,0,0.15)' : 'rgba(0,42,78,0.06)', color: pack.popular ? '#FFD700' : '#002A4E' }}>
                    {TAB_ICONS[activeTab]}
                  </div>
                </div>

                {/* Prix */}
                <div className="rounded-2xl p-5 mb-6 text-center"
                  style={{ backgroundColor: pack.popular ? 'rgba(255,215,0,0.08)' : 'rgba(0,42,78,0.04)' }}>
                  <div className="flex items-end justify-center gap-1">
                    <span className="text-6xl font-black leading-none" style={{ color: pack.popular ? '#FFD700' : '#002A4E' }}>
                      {pack.price.toLocaleString('fr-FR')}
                    </span>
                    <div className="pb-1 text-left">
                      <div className="text-sm font-bold" style={{ color: pack.popular ? 'rgba(255,215,0,0.8)' : '#6B7280' }}>FCFA</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-1.5 mt-2">
                    <svg className="w-3.5 h-3.5" fill="none" stroke={pack.popular ? 'rgba(255,215,0,0.6)' : '#9CA3AF'} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span className="text-sm" style={{ color: pack.popular ? 'rgba(255,255,255,0.5)' : '#9CA3AF' }}>{pack.duration}</span>
                  </div>
                </div>

                {/* Séparateur */}
                <div className="mb-5 h-px" style={{ backgroundColor: pack.popular ? 'rgba(255,255,255,0.08)' : '#F3F4F6' }}></div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {pack.features.map((f, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: pack.popular ? 'rgba(255,215,0,0.15)' : 'rgba(0,42,78,0.07)' }}>
                        <CheckIcon gold={pack.popular} />
                      </div>
                      <span className="text-sm" style={{ color: pack.popular ? 'rgba(255,255,255,0.8)' : '#374151' }}>{f}</span>
                    </li>
                  ))}
                </ul>

                {/* Bouton */}
                <button
                  onClick={() => handleSelectPack(pack)}
                  className="w-full py-4 rounded-2xl font-bold text-base transition-all duration-300 hover:scale-105 hover:shadow-xl flex items-center justify-center gap-2"
                  style={pack.popular
                    ? { background: 'linear-gradient(135deg, #FFD700, #f0c800)', color: '#002A4E', boxShadow: '0 8px 24px rgba(255,215,0,0.35)' }
                    : { background: 'linear-gradient(135deg, #002A4E, #003d6b)', color: '#FFD700' }
                  }
                >
                  Choisir {pack.name}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ── GARANTIES ───────────────────────────── */}
        <div ref={refBadges} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: '🔒', title: 'Paiement sécurisé', desc: 'Simple et rapide', delay: 'delay-100' },
            { icon: '⚡', title: 'Activation rapide', desc: 'Visible en quelques minutes', delay: 'delay-200' },
            { icon: '🎯', title: 'Ciblage précis', desc: 'Atteignez vos clients', delay: 'delay-300' },
            { icon: '💬', title: 'Support réactif', desc: 'Aide disponible 7j/7', delay: 'delay-400' }
          ].map(({ icon, title, desc, delay }) => (
            <div key={title} className={`bg-white rounded-2xl p-5 text-center shadow-sm hover:shadow-md transition-all hover:-translate-y-1 anim-hidden ${inBadges ? `animate-fade-in-up ${delay}` : ''}`}>
              <div className="text-3xl mb-2">{icon}</div>
              <div className="font-bold text-sm" style={{ color: '#002A4E' }}>{title}</div>
              <div className="text-xs text-gray-400 mt-1">{desc}</div>
            </div>
          ))}
        </div>

        {/* ── CTA FINAL ───────────────────────────── */}
        <div ref={refCta} className={`relative overflow-hidden rounded-3xl p-10 md:p-14 text-center anim-hidden ${inCta ? 'animate-scale-in' : ''}`} style={{ background: 'linear-gradient(135deg, #001a33 0%, #002A4E 50%, #003d6b 100%)' }}>
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,215,0,0.12) 0%, transparent 70%)' }}></div>
            <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,215,0,0.08) 0%, transparent 70%)' }}></div>
          </div>
          <div className="relative z-10">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-bold mb-5 border" style={{ backgroundColor: 'rgba(255,215,0,0.1)', color: '#FFD700', borderColor: 'rgba(255,215,0,0.3)' }}>
              Besoin d'aide ?
            </span>
            <h3 className="text-3xl md:text-4xl font-black text-white mb-4">
              Une question sur nos tarifs ?
            </h3>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              Notre équipe est disponible pour vous guider vers le pack le mieux adapté à votre activité.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/help"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-bold transition-all hover:scale-105 hover:shadow-xl"
                style={{ background: 'linear-gradient(135deg, #FFD700, #f0c800)', color: '#002A4E', boxShadow: '0 8px 24px rgba(255,215,0,0.3)' }}
              >
                Contacter le support
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                </svg>
              </Link>
              <Link
                to={ROUTES.REGISTER}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-bold border-2 text-white transition-all hover:scale-105"
                style={{ borderColor: 'rgba(255,215,0,0.4)' }}
              >
                Créer un compte gratuit
              </Link>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

export default PricingPage
